/**
 * Token Manager for Axios Interceptor
 *
 * Bridges the Auth0 token getter (which only exists inside React) to the axios
 * interceptors (which live outside React).
 *
 * Crucially it distinguishes three states, because the interceptors need to
 * treat them differently:
 *   - getter registered   -> user is (or is becoming) authenticated
 *   - getter NOT registered -> either logged out, OR auth is still rehydrating
 *     on a fresh tab and the getter has not been wired up yet
 *
 * The second case is what caused the redirect loop: a request fired during
 * rehydration got no token, 401'd, and the 401 handler relaunched Auth0.
 */

type TokenGetterFunction = () => Promise<string>

let tokenGetterFunction: TokenGetterFunction | null = null

// Resolves when a token getter is registered. Consumers can await this to hold
// a request until auth is ready instead of firing it with no token.
let readyResolve: (() => void) | null = null
let readyPromise: Promise<void> = new Promise((resolve) => {
  readyResolve = resolve
})

export const setTokenGetter = (getter: TokenGetterFunction): void => {
  tokenGetterFunction = getter
  // Release anyone waiting on readiness.
  readyResolve?.()
}

export const clearTokenGetter = (): void => {
  tokenGetterFunction = null
  // Arm a fresh readiness gate so a later sign-in can be awaited again.
  readyPromise = new Promise((resolve) => {
    readyResolve = resolve
  })
}

// True once a getter is registered (i.e. the app is authenticated and wired up).
export const isTokenGetterReady = (): boolean => tokenGetterFunction !== null

// Waits up to timeoutMs for a token getter to be registered. Returns true if
// one became available, false if it timed out (e.g. genuinely logged out).
export const waitForTokenGetter = async (timeoutMs = 4000): Promise<boolean> => {
  if (tokenGetterFunction) return true
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<false>((resolve) => {
    timer = setTimeout(() => resolve(false), timeoutMs)
  })
  const ready = readyPromise.then(() => true as const)
  const result = await Promise.race([ready, timeout])
  if (timer) clearTimeout(timer)
  return result
}

export const getToken = async (): Promise<string | null> => {
  if (!tokenGetterFunction) {
    return null
  }
  try {
    return await tokenGetterFunction()
  } catch {
    return null
  }
}
